import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  background: 'var(--navy)', border: '1px solid var(--border-2)',
  color: 'var(--text-1)', fontSize: 14, fontFamily: 'Inter, sans-serif',
  outline: 'none', boxSizing: 'border-box',
};

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email:'', password:'', name:'', country:'' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const nextUrl = searchParams.get('next') || '/';

  useEffect(() => { if (referralCode) setMode('register'); }, [referralCode]);

  // Already logged in → redirect
  useEffect(() => {
    if (localStorage.getItem('evapp_token')) navigate(nextUrl, { replace: true });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'register' && referralCode ? { ...form, referral_code: referralCode } : form;
      const res = await api.post(endpoint, payload);
      localStorage.setItem('evapp_token', res.data.token);
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      if (mode === 'register' && !localStorage.getItem('cs_onboarded')) {
        navigate('/welcome');
      } else {
        navigate(nextUrl, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 16px', fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16,
              background: 'linear-gradient(135deg,#00D4AA,#0099FF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
              boxShadow: '0 0 30px rgba(0,212,170,0.25)',
            }}>⚡</div>
            <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--text-1)', letterSpacing: '-0.4px' }}>
              Charge<span style={{ color: 'var(--teal)' }}>Sure</span>
            </span>
          </Link>
          <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 8 }}>Live EV charger reliability — globally</p>
        </div>

        {referralCode && (
          <div style={{ background: 'var(--teal-dim)', border: '1px solid var(--teal-mid)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--teal)', textAlign: 'center' }}>
            🎁 You were invited — sign up to get 20 bonus points
          </div>
        )}

        <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 18, padding: '28px 24px' }}>
          {/* Toggle */}
          <div style={{ display: 'flex', background: 'var(--navy)', borderRadius: 10, padding: 3, marginBottom: 22, border: '1px solid var(--border)' }}>
            {['login','register'].map(m => (
              <button key={m} onClick={() => setMode(m)} style={{
                flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                background: mode === m ? 'var(--slate)' : 'transparent',
                color: mode === m ? 'var(--text-1)' : 'var(--text-3)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.15s',
              }}>
                {m === 'login' ? 'Log In' : 'Sign Up'}
              </button>
            ))}
          </div>

          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {mode === 'register' && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 5 }}>Name</label>
                <input placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={inputStyle} />
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 5 }}>Email</label>
              <input type="email" required placeholder="you@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inputStyle} />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <label style={{ fontSize: 12, color: 'var(--text-3)' }}>Password</label>
                {mode === 'login' && <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--teal)', textDecoration: 'none' }}>Forgot password?</Link>}
              </div>
              <input type="password" required minLength={6} placeholder="Min. 6 characters" value={form.password} onChange={e => setForm({...form, password: e.target.value})} style={inputStyle} />
            </div>
            {mode === 'register' && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 5 }}>Country</label>
                <select value={form.country} onChange={e => setForm({...form, country: e.target.value})} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Select country</option>
                  {[['IN','🇮🇳 India'],['DE','🇩🇪 Germany'],['NL','🇳🇱 Netherlands'],['FR','🇫🇷 France'],['GB','🇬🇧 United Kingdom'],['US','🇺🇸 United States']].map(([v,l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            )}
            <button type="submit" disabled={loading} style={{
              marginTop: 4, padding: '13px 0', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#00D4AA,#0099FF)',
              color: '#F0F4FF', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 4px 16px rgba(0,212,170,0.25)',
            }}>
              {loading ? 'Please wait…' : mode === 'login' ? 'Log In' : 'Create Free Account'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--text-3)' }}>
          Charging network operator?{' '}
          <Link to="/operator" style={{ color: 'var(--teal)', textDecoration: 'none' }}>Operator portal →</Link>
        </p>
      </div>
    </div>
  );
}
